import React, { useState, useEffect } from 'react';
import { format, isValid } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { useCRM } from '../../context/CRMContext';
import Button from '../UI/Button';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../lib/supabase';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';

const { FiMessageSquare, FiSend, FiTrash2, FiEdit, FiX, FiCheck } = FiIcons;

const TaskComments = ({ taskId }) => {
  const { user: currentUser } = useAuth();
  const { users } = useCRM();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    if (taskId) loadComments();
  }, [taskId]);

  const loadComments = async () => {
    try {
      const { data, error } = await supabase
        .from('task_comments_crm_2025')
        .select(`*, users_crm_2025 (full_name, role)`)
        .eq('task_id', taskId)
        .order('created_at', { ascending: false });

      if (!error) setComments(data || []);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('task_comments_crm_2025')
        .insert([{ task_id: taskId, user_id: currentUser.id, comment: newComment.trim() }]);

      if (error) throw error;
      setNewComment('');
      loadComments();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      const { error } = await supabase.from('task_comments_crm_2025').delete().eq('id', commentId);
      if (error) throw error;
      loadComments();
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const handleUpdate = async (id) => {
    if (!editText.trim()) return;
    try {
      const { error } = await supabase.from('task_comments_crm_2025').update({ comment: editText.trim() }).eq('id', id);
      if (error) throw error;
      setEditingId(null);
      loadComments();
    } catch (error) {
      toast.error('Failed to update comment');
    }
  };

  const getUserName = (userId, userData) => {
    if (userData?.full_name) return userData.full_name;
    const user = users?.find(u => u.id === userId);
    return user?.full_name || 'Unknown User';
  };

  const formatDateSafely = (dateStr) => {
    const date = new Date(dateStr);
    return isValid(date) ? format(date, 'MMM dd, HH:mm') : 'Recently';
  };

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-gray-900 flex items-center">
        <SafeIcon icon={FiMessageSquare} className="h-4 w-4 mr-2" />
        Comments ({comments.length})
      </h4>
      <form onSubmit={handleSubmit} className="space-y-2">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          rows={2}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary-500 text-sm"
        />
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={!newComment.trim() || isSubmitting}>
            Add Comment
          </Button>
        </div>
      </form>
      <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
        {loading ? (
          <p className="text-xs text-gray-500 text-center">Loading...</p>
        ) : (
          comments.map(comment => (
            <div key={comment.id} className="bg-gray-50 rounded-lg p-3 group">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-bold text-gray-900">{getUserName(comment.user_id, comment.users_crm_2025)}</span>
                  <span className="text-[10px] text-gray-400">{formatDateSafely(comment.created_at)}</span>
                </div>
                {currentUser.id === comment.user_id && (
                  <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => { setEditingId(comment.id); setEditText(comment.comment); }} className="text-gray-400 hover:text-primary-600"><SafeIcon icon={FiEdit} className="h-3 w-3" /></button>
                    <button onClick={() => handleDelete(comment.id)} className="text-gray-400 hover:text-red-600"><SafeIcon icon={FiTrash2} className="h-3 w-3" /></button>
                  </div>
                )}
              </div>
              {editingId === comment.id ? (
                <div className="mt-1">
                  <textarea value={editText} onChange={(e) => setEditText(e.target.value)} className="w-full p-2 border border-primary-300 rounded text-sm" rows={2} autoFocus />
                  <div className="flex justify-end space-x-2 mt-1">
                    <button onClick={() => setEditingId(null)} className="text-xs text-gray-500">Cancel</button>
                    <button onClick={() => handleUpdate(comment.id)} className="text-xs text-primary-600 font-bold">Save</button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.comment}</p>
              )}
            </div>
          ))
        )}
      </div>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 3px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #ddd; border-radius: 3px; }
      `}</style>
    </div>
  );
};

export default TaskComments;